import React, { useCallback, useMemo, useState } from 'react'
import classnames from 'classnames'
import PropTypes from 'prop-types'
import { constants, dateFormatter } from 'helpers'

import {
  CloseIcon,
  CopyPublicProfileLink,
  HeartIcon,
  LocationAlt2Icon,
  VerifiedIcon,
} from 'components'
import BriefcaseIcon from './components/BriefcaseIcon'
import EducationIcon from './components/EducationIcon'
import PhotoGallery from './components/PhotoGallery'
import SmallLoading from '../EventCard/EventCardSmallLoading'
import ProfileTags from './components/ProfileTags'
import SocialLinks from 'components/ProfileInfo/components/SocialLinks'
import LikesModal from 'components/LikesModal'

const InfoSection = ({ Icon, gradient, label, text }) => (
  <div className="mb-6 flex items-center">
    <div className="relative bg-midnightBlack w-10 h-10 rounded-full p-3 mr-4 flex justify-center items-center overflow-hidden">
      <div className="w-full h-full inset-0 absolute" style={{ background: gradient }} />
      <Icon className="relative w-6 h-6 text-white" />
    </div>
    <div>
      <div className="text-sm text-lightGray mb-1">{label}</div>
      <div className="text-base text-white">{text}</div>
    </div>
  </div>
)

const CONTAINER_CLASS = 'z-0 w-full inset-0 h-full overflow-x-hidden relative scrollbar-hidden'

const ProfileInfo = ({ user, onClose, onLike, liked, classes, showWaitlistStatus, children }) => {
  const [showLikesModal, setShowLikesModal] = useState(false)
  const age = useMemo(() => dateFormatter.getAgeFromBirthdate(user?.birthdate), [user?.birthdate])

  const handleLikeModalClose = useCallback(
    liked => {
      setShowLikesModal(false)
      liked && onLike(liked)
    },
    [onLike]
  )

  if (!user) {
    return (
      <div className={CONTAINER_CLASS}>
        <SmallLoading className="w-full h-auto" />
      </div>
    )
  }

  return (
    <div className={classnames(CONTAINER_CLASS, classes.container)}>
      {onClose && (
        <button
          className="absolute z-10 top-4 right-4 rounded-full bg-black bg-opacity-80 p-2 w-8 h-8 flex justify-center items-center cursor-pointer hover:opacity-80 focus:outline-none"
          onClick={onClose}
        >
          <CloseIcon className="text-white w-3 h-3" />
        </button>
      )}
      <PhotoGallery images={user.images} />
      <div className="p-4 pt-10">
        {showWaitlistStatus && (
          <div className="mb-4 flex">
            <div
              className={classnames(
                'text-white border-white border-2 border-solid border-opacity-40 py-0.5 rounded-3xl px-4 uppercase',
                {
                  'border-frolyPink border-opacity-90 font-semibold px-6':
                    user.waitlistStatus === constants.WAITLIST_STATUS.YES,
                }
              )}
            >
              {user.waitlistStatus === constants.WAITLIST_STATUS.YES ? 'Accepted' : 'Not Accepted'}
            </div>
          </div>
        )}
        <div className="flex mb-5 w-full justify-between items-center">
          <div className="text-white">
            <div className="text-3xl font-ownersWide font-bold mb-1.5">
              <span>{user.name}</span>
              {age && <span className="text-lightGray"> {age}</span>}
              {user.isVerifiedAccount && <VerifiedIcon className="w-4 h-4 ml-2" />}
            </div>
            {user?.username && (
              <CopyPublicProfileLink username={user.username} className="opacity-50" />
            )}
          </div>
          {onLike && (
            <div className="flex items-center space-x-2.5">
              {!liked && (
                <button
                  className="flex items-center justify-center w-12 h-12 rounded-full border border-solid border-gray-700  bg-transparent"
                  onClick={() => setShowLikesModal(true)}
                >
                  <HeartIcon className="text-azureBlue w-5" />
                </button>
              )}
            </div>
          )}
        </div>
        {user.profile?.bio && (
          <div className="text-sm opacity-70 mb-6 text-lightGray break-words">
            {user.profile.bio}
          </div>
        )}
        <ProfileTags user={user} />
        {user.location && (
          <InfoSection
            Icon={LocationAlt2Icon}
            gradient="linear-gradient(139.99deg, #FF9494 -11.18%, rgba(23, 26, 30, 0) 103.57%)"
            label="Current City"
            text={user.location}
          />
        )}
        {user.profile?.occupation && (
          <InfoSection
            Icon={BriefcaseIcon}
            gradient="linear-gradient(139.99deg, #326EB2 -11.18%, rgba(23, 26, 30, 0) 103.57%)"
            label={user.profile.occupationPosition ? user.profile.occupation : 'Occupation'}
            text={user.profile.occupationPosition || user.profile.occupation}
          />
        )}
        {user.profile?.education && (
          <InfoSection
            Icon={EducationIcon}
            gradient="linear-gradient(139.99deg, #CFE0E2 -11.18%, rgba(23, 26, 30, 0) 103.57%)"
            label={user.profile?.educationLevel || 'Education'}
            text={user.profile.education}
          />
        )}
        <SocialLinks user={user} />
        {children}
      </div>
      <LikesModal profile={user} show={showLikesModal} onClose={handleLikeModalClose} />
    </div>
  )
}

ProfileInfo.defaultProps = {
  classes: {},
}

ProfileInfo.propTypes = {
  classes: PropTypes.object,
  hostEvents: PropTypes.array,
  user: PropTypes.object,
  onClose: PropTypes.func,
  onLike: PropTypes.func,
  refetch: PropTypes.func,
  liked: PropTypes.bool,
  children: PropTypes.node,
}

export default ProfileInfo
